import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';

@Component({
  standalone: false,
  selector: 'app-rbac',
  templateUrl: './rbac.component.html',
  styleUrl: './rbac.component.scss'
})
export class RbacComponent implements OnInit {

  screenNames: string[] = [];
  isDisabled = true;
  errorMessage: any;
  successMessage: any;
  dataArray: any[] = [];

  // New properties for role and screen management
  newRoleName: string = '';
  newRoleDepartment: string = '';
  newScreenName: string = '';

  constructor(private apiService: ApiService) { }

  ngOnInit() {
    this.fetchRBACData();
  }

  fetchRBACData() {
    this.apiService.getData('/admin/rbac').subscribe(
      (response: any) => {
        if (!response.error) {
          // Extract unique screen names from the response
          const screensSet = new Set<string>();
          response.data.forEach((item: any) => {
            item.access.forEach((acc: any) => {
              screensSet.add(acc.screen);
            });
          });
          this.screenNames = Array.from(screensSet).sort();

          // Normalize the data to ensure all access values are boolean
          // and every role has access entries for all screens
          this.dataArray = response.data.map((item: any) => {
            const roleAccess = item.access.map((acc: any) => ({
              ...acc,
              access: typeof acc.access === 'boolean' ? acc.access : 
                     (acc.access && acc.access.read) ? true : false
            }));

            // Add missing screen access entries
            const accessMap = new Map<string, any>();
            roleAccess.forEach((acc: any) => {
              accessMap.set(acc.screen, acc);
            });

            this.screenNames.forEach((screenName: string) => {
              if (!accessMap.has(screenName)) {
                accessMap.set(screenName, {
                  screen: screenName,
                  access: false
                });
              }
            });

            // Sort the access entries to match the screenNames order
            const sortedAccess = this.screenNames.map((screenName: string) => 
              accessMap.get(screenName)
            );

            return {
              ...item,
              access: sortedAccess
            };
          });
        } else {
          this.errorMessage = response.message || 'Failed to fetch RBAC data';
        }
      },
      (error: any) => {
        this.errorMessage = 'Error fetching RBAC data: ' + (error.message || error);
      }
    );
  }

  onEdit() {
    this.isDisabled = false;
  }

  onSave() {
    this.apiService.postData('/admin/rbac/update', this.dataArray).subscribe(
      (response: any) => {
        if (!response.error) {
          this.successMessage = response.message || 'RBAC updated successfully';
          this.isDisabled = true;
          // Refresh the page after 1 second to show the starting state
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          this.errorMessage = response.message || 'Failed to update RBAC';
        }
      },
      (error: any) => {
        this.errorMessage = 'Error updating RBAC: ' + (error.message || error);
      }
    );
  }

  // Role management methods
  createRole() {
    if (!this.newRoleName || !this.newRoleDepartment) {
      this.errorMessage = 'Please enter both role name and department';
      return;
    }

    this.apiService.postData('/admin/rbac/roles', {
      name: this.newRoleName,
      department: this.newRoleDepartment
    }).subscribe(
      (response: any) => {
        if (response.success) {
          this.successMessage = 'Role created successfully';
          this.newRoleName = '';
          this.newRoleDepartment = '';
          this.fetchRBACData(); // Refresh data
        } else {
          this.errorMessage = response.message || 'Failed to create role';
        }
      },
      (error: any) => {
        this.errorMessage = 'Error creating role: ' + (error.message || error);
      }
    );
  }

  deleteRole(roleId: string) {
    if (confirm('Are you sure you want to delete this role?')) {
      this.apiService.deleteData(`/admin/rbac/roles/${roleId}`).subscribe(
        (response: any) => {
          if (response.success) {
            this.successMessage = 'Role deleted successfully';
            this.fetchRBACData(); // Refresh data
          } else {
            this.errorMessage = response.message || 'Failed to delete role';
          }
        },
        (error: any) => {
          this.errorMessage = 'Error deleting role: ' + (error.message || error);
        }
      );
    }
  }

  // Screen management methods
  createScreen() {
    if (!this.newScreenName) {
      this.errorMessage = 'Please enter screen name';
      return;
    }

    this.apiService.postData('/admin/rbac/screens', {
      name: this.newScreenName
    }).subscribe(
      (response: any) => {
        if (response.success) {
          this.successMessage = 'Screen created successfully';
          this.newScreenName = '';
          this.screenNames.push(this.newScreenName);
          this.fetchRBACData(); // Refresh data
        } else {
          this.errorMessage = response.message || 'Failed to create screen';
        }
      },
      (error: any) => {
        this.errorMessage = 'Error creating screen: ' + (error.message || error);
      }
    );
  }

  deleteScreen(screenId: string) {
    if (confirm('Are you sure you want to delete this screen?')) {
      this.apiService.deleteData(`/admin/rbac/screens/${screenId}`).subscribe(
        (response: any) => {
          if (response.success) {
            this.successMessage = 'Screen deleted successfully';
            this.fetchRBACData(); // Refresh data
          } else {
            this.errorMessage = response.message || 'Failed to delete screen';
          }
        },
        (error: any) => {
          this.errorMessage = 'Error deleting screen: ' + (error.message || error);
        }
      );
    }
  }

}
